import React from 'react';
import { Box, Typography, Link } from '@mui/material';

const TermsOfUse = () => {
    // Check if the URL includes '?webview=true'
    const searchParams = new URLSearchParams(window.location.search);
    const isWebView = searchParams.get('webview') === 'true';

    // Helper function to append ?webview=true to links
    const appendWebViewParam = (url) => (isWebView ? `${url}?webview=true` : url);

    return (
        <>
            <Box sx={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
                <Typography variant="h5" gutterBottom sx={{ paddingTop: '16px', paddingBottom: '8px' }}>
                    Instapath.io Terms of Use
                </Typography>
                <Typography variant="body2" sx={{ marginBottom: 4, color: '#6c757d' }}>
                    Effective Date: December 22, 2024
                </Typography>

                <Typography variant="body1" component="p" gutterBottom>
                    Please read these Terms of Use (“Terms”) carefully before using the instapath.io website,
                    owned and operated by Rigg Dynamics LLC, or any of its associated services (collectively, the “Service”).
                    By accessing or using the Service, you agree to be bound by these Terms. If you do not agree to these Terms,
                    you may not access or use the Service.
                </Typography>

                <Typography variant="h5" gutterBottom sx={{ paddingTop: '16px', paddingBottom: '8px' }}>
                    1. Acceptance of Terms
                </Typography>
                <Typography variant="body1" component="p" gutterBottom>
                    By accessing or using the Service, you agree to these Terms and our{' '}
                    <Link href={appendWebViewParam('/privacy-policy')}>Privacy Policy</Link>, which is incorporated into these Terms by reference.
                    If you do not agree to these Terms or the Privacy Policy, you may not access or use the Service.
                </Typography>

                <Typography variant="h5" gutterBottom sx={{ paddingTop: '16px', paddingBottom: '8px' }}>
                    2. Use of the Service
                </Typography>
                <Typography variant="body1" component="p" gutterBottom>
                    You may use the Service only for lawful purposes and in accordance with these Terms. You may not use the Service:
                </Typography>
                <ul style={{ margin: 0, paddingLeft: '16px' }}>
                    <li>In any way that violates any applicable federal, state, local, or international law or regulation.</li>
                    <li>For the purpose of exploiting, harming, or attempting to exploit or harm minors in any way by exposing them to inappropriate content, asking for personally identifiable information, or otherwise.</li>
                    <li>To transmit, or procure the sending of, any advertising or promotional material, including any “junk mail,” “chain letter,” “spam,” or any other similar solicitation.</li>
                    <li>To impersonate or attempt to impersonate Rigg Dynamics LLC, a Rigg Dynamics LLC employee, another user, or any other person or entity (including, without limitation, by using email addresses associated with any of the foregoing).</li>
                    <li>To engage in any other conduct that restricts or inhibits anyone’s use or enjoyment of the Service, or which, as determined by Rigg Dynamics LLC, may harm Rigg Dynamics LLC or users of the Service or expose them to liability.</li>
                </ul>
                <Typography variant="h5" gutterBottom sx={{ paddingTop: '16px', paddingBottom: '8px' }}>
                    3. User Accounts
                </Typography>
                <Typography variant="body1" component="p" gutterBottom>
                    To access certain features of the Service, you may be required to create a user account. You are responsible for maintaining the confidentiality of your account login information and are fully responsible for all activities that occur under your account. You agree to notify Rigg Dynamics LLC immediately of any unauthorized use of your account or any other breach of security. Rigg Dynamics LLC will not be liable for any loss or damage arising from your failure to comply with this Section.
                </Typography>

                <Typography variant="h5" gutterBottom sx={{ paddingTop: '16px', paddingBottom: '8px' }}>
                    4. Intellectual Property
                </Typography>
                <Typography variant="body1" component="p" gutterBottom>
                    The Service and its original content, features, and functionality are owned by Rigg Dynamics LLC and are protected by international copyright, trademark, patent, trade secret, and other intellectual property or proprietary rights laws.
                </Typography>

                <Typography variant="h5" gutterBottom sx={{ paddingTop: '16px', paddingBottom: '8px' }}>
                    5. Disclaimer of Warranties
                </Typography>
                <Typography variant="body1" component="p" gutterBottom>
                    The Service is provided on an “as is” and “as available” basis. Rigg Dynamics LLC makes no representations or warranties of any kind, express or implied, as to the operation of the Service or the information, content, materials, or products included on the Service.
                </Typography>

                <Typography variant="h5" gutterBottom sx={{ paddingTop: '16px', paddingBottom: '8px' }}>
                    6. Limitation of Liability
                </Typography>
                <Typography variant="body1" component="p" gutterBottom>
                    Rigg Dynamics LLC will not be liable for any damages of any kind arising from the use of the Service, including, but not limited to direct, indirect, incidental, punitive, and consequential damages.
                </Typography>

                <Typography variant="h5" gutterBottom sx={{ paddingTop: '16px', paddingBottom: '8px' }}>
                    7. Indemnification
                </Typography>
                <Typography variant="body1" component="p" gutterBottom>
                    You agree to indemnify, defend, and hold harmless Rigg Dynamics LLC, its affiliates, officers, directors, employees, consultants, agents, and licensors from any and all claims, liabilities, damages, losses, costs, expenses, fees (including reasonable attorneys’ fees and costs) that such parties may incur as a result of or arising from your (or anyone using your account’s) violation of these Terms.
                </Typography>

                <Typography variant="h5" gutterBottom sx={{ paddingTop: '16px', paddingBottom: '8px' }}>
                    8. Governing Law
                </Typography>
                <Typography variant="body1" component="p" gutterBottom>
                    These Terms shall be governed and construed in accordance with the laws of the State of Oregon, without regard to its conflict of law provisions.
                </Typography>

                <Typography variant="h5" gutterBottom sx={{ paddingTop: '16px', paddingBottom: '8px' }}>
                    9. Changes to Terms
                </Typography>
                <Typography variant="body1" component="p" gutterBottom>
                    Rigg Dynamics LLC reserves the right, at its sole discretion, to modify or replace these Terms at any time. If a revision is material, we will try to provide at least 30 days’ notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
                </Typography>

                <Typography variant="h5" gutterBottom sx={{ paddingTop: '16px', paddingBottom: '8px' }}>
                    10. Contact Us
                </Typography>
                <Typography variant="body1" component="p" gutterBottom>
                    If you have any questions about these Terms, please contact us.
                </Typography>

                <Typography variant="body2" align="center" sx={{ marginTop: 4, color: '#6c757d' }}>
                    By continuing to access or use our Service after those revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, in whole or in part, please stop using the website and the Service.
                </Typography>
            </Box>
        </>
    );
};

export default TermsOfUse;